import { useEffect, useRef } from 'react';
import { useAuth } from '../auth';
import { useI18n, readStoredLocale } from '../i18n';
import { useTheme, readStoredTheme } from '../theme';

/**
 * Hydratation descendante des préférences (langue + thème) depuis le profil serveur
 * (`/api/auth/me`, champs `user.locale`/`user.theme`).
 *
 * `localStorage` reste la SOURCE PILOTE : on n'applique la valeur serveur que si
 * l'utilisateur n'a AUCUNE préférence locale explicite (`readStored…() === null`).
 * Dès qu'une valeur est appliquée, elle est persistée localement et devient la
 * préférence pilote — la sync est donc uniquement descendante (pas de route
 * d'écriture en T2). Ne rend rien.
 */
export function PreferenceBridge(): null {
  const { user } = useAuth();
  const { setLocale } = useI18n();
  const { setTheme } = useTheme();
  // Évite de ré-appliquer pour un même compte (le premier `set…` persiste la valeur,
  // donc `readStored…` cesse de renvoyer `null` ensuite ; le garde-fou couvre les
  // re-rendus intercalaires avant l'écriture synchrone).
  const hydratedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user || hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;
    if (readStoredLocale() === null) setLocale(user.locale);
    if (readStoredTheme() === null) setTheme(user.theme);
  }, [user, setLocale, setTheme]);

  return null;
}
